/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Collapse } from "ui/Collapse";

import { Checkbox } from "../Checkbox";
import { CheckboxField } from "../CheckboxField";
import { ArrowIcon } from "./components/ArrowIcon";
import { MinusIcon } from "./components/MinusIcon";
import { PlusIcon } from "./components/PlusIcon";
import { CLASS_NAMES } from "./constants/classNames";
import {
  areaOtherStyle,
  areaStyle,
  prefecturesStyle,
  rootStyle,
  rootTitleStyle,
  arrowIconWrapperStyle,
  downArrowIconWrapperStyle,
  prefectureStyle,
  plusIconWrapperStyle,
  minusIconWrapperStyle,
} from "./styles";
import { useThis } from "./useThis";

import type { Props } from "./props";
import type { FcWithoutChildren } from "types/fcWithoutChildren";

const { AREA_COLLAPSE } = CLASS_NAMES;

const rootCloseIcon = (
  <span css={arrowIconWrapperStyle}>
    <ArrowIcon />
  </span>
);

const rootOpenIcon = (
  <span css={downArrowIconWrapperStyle}>
    <ArrowIcon />
  </span>
);

const areaOpenIcon = (
  <span css={plusIconWrapperStyle}>
    <PlusIcon />
  </span>
);

const areaCloseIcon = (
  <span css={minusIconWrapperStyle}>
    <MinusIcon />
  </span>
);

export const PrefecturesSelector: FcWithoutChildren<Props> = (props) => {
  const { groupedPrefectures, onCheckArea, onCheckPrefecture, onCollapseArea } =
    props;
  const { handleToggleRootCollapse, isOpenedCollapse, rootCollapseTitle } =
    useThis();
  const rootCollapseTitleButton = (
    <button
      css={rootTitleStyle}
      type="button"
      onClick={handleToggleRootCollapse}
    >
      {rootCollapseTitle}
    </button>
  );

  return (
    <div css={rootStyle}>
      <Collapse
        className={AREA_COLLAPSE}
        closeIcon={rootCloseIcon}
        handleToggleOpenAndClose={handleToggleRootCollapse}
        id={"prefecturesSelector"}
        isOpened={isOpenedCollapse}
        openIcon={rootOpenIcon}
        title={rootCollapseTitleButton}
      >
        <ul>
          {groupedPrefectures.map((group) => {
            const {
              code: areaCode,
              isAreaChecked,
              isOpened = false,
              name: areaName,
              prefectures,
            } = group;
            const handleToggleCheckedArea = () => {
              onCheckArea(areaCode);
            };
            const areaCheckboxField = (
              <CheckboxField
                checkboxElement={
                  <Checkbox
                    checked={isAreaChecked}
                    onChange={handleToggleCheckedArea}
                  />
                }
                labelText={areaName}
              />
            );

            if (prefectures.length === 0) {
              return (
                <li key={areaName} css={areaStyle}>
                  <span css={areaOtherStyle}>{areaCheckboxField}</span>
                </li>
              );
            }

            const handleToggleOpenAndClose = () => {
              onCollapseArea(areaCode);
            };

            return (
              <li key={areaName} css={areaStyle}>
                <Collapse
                  className={AREA_COLLAPSE}
                  closeIcon={areaCloseIcon}
                  handleToggleOpenAndClose={handleToggleOpenAndClose}
                  id={areaName}
                  isOpened={isOpened}
                  openIcon={areaOpenIcon}
                  title={areaCheckboxField}
                >
                  <ul css={prefecturesStyle}>
                    {prefectures.map((prefecture) => {
                      const {
                        isPrefectureChecked,
                        code,
                        name: prefectureName,
                      } = prefecture;
                      const handleToggleCheckedPrefecture = () => {
                        onCheckPrefecture(code);
                      };
                      return (
                        <li key={code}>
                          <CheckboxField
                            checkboxElement={
                              <Checkbox
                                checked={isPrefectureChecked}
                                onChange={handleToggleCheckedPrefecture}
                              />
                            }
                            labelText={
                              <span css={prefectureStyle}>
                                {prefectureName}
                              </span>
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                </Collapse>
              </li>
            );
          })}
        </ul>
      </Collapse>
    </div>
  );
};
